import {STATUS} from '@jobseeker/common-core/constants';
import axiosInterceptorInstance from '@jobseeker/common-core/helpers/axiosInterceptor';

const axios = axiosInterceptorInstance(
  process.env.NEXT_PUBLIC_COURSES_API_BASE_URL
);

// TODO: Combine the functions into one, as the code is getting repeated
const getCourses = async ({
  userId,
  token,
  headers = {},
  params = {},
}: {
  userId: number;
  token: string;
  headers?: object;
  params?: object;
}): Promise<any> => {
  try {
    const response: any = await axios.get(`/v2/course/${userId}/search`, {
      headers: {Authorization: `Bearer ${token}`, ...headers},
      withCredentials: true,
      params: {...params},
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getUnpublishedCourses = async ({
  params = {},
}: {
  params?: object;
}): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/course/unpublish', {
      params,
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getCourseDetails = async ({
  coursePublishedId,
  userId,
  token,
  headers,
  refinv,
  reforg,
  forUnpublishedCourse = false,
}: {
  coursePublishedId: number;
  userId: number;
  token: string;
  headers?: object;
  refinv?: string;
  reforg?: string;
  forUnpublishedCourse?: boolean;
}): Promise<any> => {
  try {
    const apiPath = forUnpublishedCourse
      ? `/v2/course/unpublish/${coursePublishedId}/detail`
      : `/v2/course/${userId}/user/${coursePublishedId}/detail`;

    const response: any = await axios.get(apiPath, {
      headers: {Authorization: `Bearer ${token}`, ...headers},
      params: {refinv, reforg},
      withCredentials: true,
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const downloadBrochure = async ({
  coursePublishedId,
  token = '',
  refinv = 'NA',
  reforg = 'NA',
}: {
  coursePublishedId: number;
  token?: string;
  refinv?: string;
  reforg?: string;
}): Promise<any> => {
  try {
    const response = await axios.get(
      `/v2/course/${coursePublishedId}/brochure`,
      {
        headers: {Authorization: `Bearer ${token}`},
        params: {
          refinv,
          reforg,
        },
        withCredentials: true,
      }
    );
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const enquireCourse = async ({
  coursePublishedId,
  token,
  refinv = 'NA',
  reforg = 'NA',
}: {
  coursePublishedId: number;
  token: string;
  refinv?: string;
  reforg?: string;
}): Promise<any> => {
  try {
    const response = await axios.get(
      `/v2/course/${coursePublishedId}/enquiry`,
      {
        headers: {Authorization: `Bearer ${token}`},
        params: {
          refinv,
          reforg,
        },
        withCredentials: true,
      }
    );
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getAllCategoriesCourses = async ({token}: {token: string}) => {
  try {
    const response: any = await axios.get('/v2/homePage/allcat', {
      headers: {Authorization: `Bearer ${token}`},
      withCredentials: true,
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getCategoryCourses = async ({
  token,
  categoryId,
  blackListedCourse,
}: {
  token: string;
  categoryId: number;
  blackListedCourse?: string;
}) => {
  try {
    const response: any = await axios.get(`/v2/courses/${categoryId}`, {
      headers: {Authorization: `Bearer ${token}`},
      params: {
        ...(blackListedCourse && {blackListedCourse: 1}),
      },
      withCredentials: true,
    });

    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getUnappliedSimilarCourses = async ({
  userId,
  token,
  courseIds,
}: {
  userId: number;
  token: string;
  courseIds: string;
}) => {
  try {
    const response: any = await axios.get(
      `/v2/course/${userId}/${courseIds}/checkappliedcourses`,
      {
        headers: {Authorization: `Bearer ${token}`},
        withCredentials: true,
      }
    );
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const enquireMultipleCourses = async ({
  token,
  coursePublishedIds,
  toSkipIds,
  refinv = 'NA',
  reforg = 'NA',
  device = 'Web',
}: {
  token: string;
  coursePublishedIds: number[];
  toSkipIds: number[];
  refinv?: string;
  reforg?: string;
  device?: string;
}) => {
  try {
    if (coursePublishedIds.length === 0) {
      throw {
        response: {
          data: {
            error_msg: 'Please select a course',
            status: 400,
          },
        },
      };
    }

    const response = await axios.post(
      '/v2/courseenquiry',
      {
        courseIds: JSON.stringify(coursePublishedIds),
        toSkipIds: JSON.stringify(toSkipIds),
        refinv,
        reforg,
        device,
      },
      {
        headers: {Authorization: `Bearer ${token}`},
        withCredentials: true,
      }
    );
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error_msg,
        code: error.response?.data.status,
      },
    };
  }
};

const getTopInstitutes = async (): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/homepage/topinstitute');
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getTrendingCourses = async (): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/homepage/trending');
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getBanner = async (): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/homepage/banner');
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getFeaturedCourses = async (): Promise<any> => {
  try {
    const response: any = await axios.get('/v2/homepage/featured');
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getJobReadyCourses = async ({
  userId,
  jobId,
  headers = {},
  params = {},
}: {
  userId: number;
  jobId: number;
  headers?: object;
  params?: object;
}): Promise<any> => {
  try {
    const response: any = await axios.get(
      `/v2/course/${userId}/jobready/${jobId}`,
      {headers, params}
    );
    if (response[STATUS] === 200) {
      return response.data;
    }
    return {data: {}};
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const courseService = {
  getCourses,
  enquireCourse,
  downloadBrochure,
  getCourseDetails,
  enquireMultipleCourses,
  getAllCategoriesCourses,
  getCategoryCourses,
  getUnappliedSimilarCourses,
  getTopInstitutes,
  getTrendingCourses,
  getBanner,
  getFeaturedCourses,
  getUnpublishedCourses,
  getJobReadyCourses,
};

export default courseService;
